// src/pages/Home.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { isAuthed } from '../auth';
import { apiPost } from '../api';
import '../styles/theme.css';
import '../styles/cosmic.css';
import '../styles/Home.css';
import { Element, scroller } from 'react-scroll';

function Spinner() {
  return (
    <>
      <style>{`
        @keyframes _spin{to{transform:rotate(360deg)}}
        .btn-spinner{
          display:inline-block;width:14px;height:14px;
          border:2px solid currentColor;border-top-color:transparent;
          border-radius:50%;vertical-align:-2px;margin-right:8px;
          animation:_spin 1s linear infinite
        }
      `}</style>
      <span className="btn-spinner" />
    </>
  );
}

// Move words array outside component to prevent recreating it on every render
const words = ['intention', 'purpose', 'clarity', 'presence'];

export default function Home() {
  const navigate = useNavigate();
  const location = useLocation();

  // Start hidden to prevent flash
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    // Small delay to prevent flash when navigating
    const timer = setTimeout(() => setIsReady(true), 50);
    return () => clearTimeout(timer);
  }, []);

  // Typing animation state
  const [wordIndex, setWordIndex] = useState(0);
  const [text, setText] = useState('intention'); // Start with first word
  const [isDeleting, setIsDeleting] = useState(false);
  const [showCursor, setShowCursor] = useState(true);
  const [isPaused, setIsPaused] = useState(false);

  // Inline checkout state
  const [loadingPlan, setLoadingPlan] = useState(null); // 'basic' | 'premium' | null
  const [errorMsg, setErrorMsg] = useState('');

  async function startCheckout(plan) {
    if (loadingPlan) return;
    setErrorMsg('');
    setLoadingPlan(plan);
    try {
      const res = await apiPost('/billing/checkout/session', { plan });
      const url = res?.url;
      if (!url) throw new Error(res?.error || 'No checkout URL returned');
      window.location.assign(url);
    } catch (e) {
      setErrorMsg(e?.message || 'Failed to start checkout');
      setLoadingPlan(null);
    }
  }

  function handleSubscribe(plan) {
    if (isAuthed()) {
      // Already logged in → go straight to Stripe
      startCheckout(plan);
    } else {
      // Not logged in → go to Register with plan & after=checkout so OTP success jumps straight to Stripe
      navigate(`/register?plan=${encodeURIComponent(plan)}&after=checkout`);
    }
  }

  // Auto-typer
  useEffect(() => {
    const currentWord = words[wordIndex];

    // Cursor blink effect
    const cursorInterval = setInterval(() => {
      setShowCursor((prev) => !prev);
    }, 530);

    if (isPaused) {
      return () => clearInterval(cursorInterval);
    }

    const handleTyping = () => {
      if (!isDeleting) {
        if (text.length < currentWord.length) {
          setText(currentWord.substring(0, text.length + 1));
        } else {
          setIsPaused(true);
          setTimeout(() => {
            setIsPaused(false);
            setIsDeleting(true);
          }, 2000);
        }
      } else {
        if (text.length > 0) {
          setText(currentWord.substring(0, text.length - 1));
        } else {
          setIsDeleting(false);
          setWordIndex((prev) => (prev + 1) % words.length);
        }
      }
    };

    const typingSpeed = isDeleting ? 60 + Math.random() * 40 : 100 + Math.random() * 100;

    const typingTimeout = setTimeout(handleTyping, typingSpeed);

    return () => {
      clearTimeout(typingTimeout);
      clearInterval(cursorInterval);
    };
  }, [text, isDeleting, wordIndex, isPaused]);

  // Helper: smooth scroll with header offset (used for hash + nav state)
  const scrollToIdWithOffset = (id) => {
    scroller.scrollTo(id, {
      duration: 500,
      delay: 0,
      smooth: true,
      offset: -80, // Header offset
    });
  };

  // Scroll to hash section after page loads (e.g. direct visit to /#pricing)
  useEffect(() => {
    if (!window.location.hash) return;

    const id = window.location.hash.substring(1);
    setTimeout(() => {
      scrollToIdWithOffset(id);
    }, 900); // After loading screen
  }, []);

  // Scroll when coming from another route via navigate('/', { state: { scrollTo } })
  useEffect(() => {
    if (!isReady) return;

    const targetId = location.state && location.state.scrollTo;
    if (!targetId) return;

    // Small timeout to ensure layout is settled
    setTimeout(() => {
      scrollToIdWithOffset(targetId);
    }, 50);
  }, [location, isReady]);

  return (
    <>
      <Header />
      <main className="cosmic-bg home-main">
        {/* <Background variant="home" /> */}

      {/* Hero Section */}
      <section id="home" className="hero container">
        <div className="hero-content">
          <div className="hero-badge">
            <span className="badge-dot"></span>
            Mindful messaging for modern life
          </div>
          <h1 className="hero-title">
            Start your day with
            <br />
            <span className="gradient-text">
              {text}
              <span className="typing-cursor" style={{ opacity: showCursor ? 1 : 0 }}>
                |
              </span>
            </span>
          </h1>
          <p className="hero-subtitle">
            Receive thoughtfully crafted messages each morning. Grounding wisdom that meets you exactly where you are—no apps, no complexity, just clarity.
          </p>
          <div className="hero-cta">
            <a
              href="#pricing"
              className="btn btn-primary"
              onClick={(e) => {
                e.preventDefault();
                scrollToIdWithOffset('pricing');
              }}
            >
              Begin Your Journey
              <span className="btn-arrow">→</span>
            </a>

            <Link to="/register" className="btn btn-secondary">
              Create Account
            </Link>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section id="features" className="features-section container">
        <div className="section-header">
          <h2 className="section-title">Everything you need, nothing you don't</h2>
        </div>

        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
              </svg>
            </div>
            <h3 className="feature-title">Daily SMS Delivery</h3>
            <p className="feature-desc">One thoughtful message arrives each morning. No apps to download, no notifications to manage.</p>
          </div>

          <div className="feature-card">
            <div className="feature-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <path d="M12 6v6l4 2"></path>
              </svg>
            </div>
            <h3 className="feature-title">Perfect Timing</h3>
            <p className="feature-desc">Choose your ideal delivery time. Start your day centered, or find calm whenever you need it.</p>
          </div>

          <div className="feature-card">
            <div className="feature-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
            </div>
            <h3 className="feature-title">Personalized Wisdom</h3>
            <p className="feature-desc">Optional birth chart integration tailors messages to your cosmic blueprint and current transits.</p>
          </div>

          <div className="feature-card">
            <div className="feature-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
              </svg>
            </div>
            <h3 className="feature-title">Flexible Plans</h3>
            <p className="feature-desc">Pause or cancel anytime. No contracts, no pressure—just support when you want it.</p>
          </div>

          <div className="feature-card">
            <div className="feature-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
                <path d="M2 17l10 5 10-5"></path>
                <path d="M2 12l10 5 10-5"></path>
              </svg>
            </div>
            <h3 className="feature-title">Curated Themes</h3>
            <p className="feature-desc">From mindfulness to manifestation, each message draws from ancient wisdom and modern psychology.</p>
          </div>

          <div className="feature-card">
            <div className="feature-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
              </svg>
            </div>
            <h3 className="feature-title">Privacy First</h3>
            <p className="feature-desc">Your data stays yours. No tracking, no selling, no third-party access. Ever.</p>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="how-section container">
        <div className="section-header">
          <h2 className="section-title">Three steps to daily wisdom</h2>
        </div>

        <div className="steps-container">
          <div className="step">
            <div className="step-number">01</div>
            <h3 className="step-title">Choose Your Plan</h3>
            <p className="step-desc">Select Basic for universal wisdom or Premium for personalized cosmic guidance.</p>
          </div>
          <div className="step-connector"></div>
          <div className="step">
            <div className="step-number">02</div>
            <h3 className="step-title">Set Your Time</h3>
            <p className="step-desc">Pick when you want your daily message. Most choose early morning for intention-setting.</p>
          </div>
          <div className="step-connector"></div>
          <div className="step">
            <div className="step-number">03</div>
            <h3 className="step-title">Receive & Reflect</h3>
            <p className="step-desc">Your message arrives via SMS. Read, breathe, and carry that wisdom throughout your day.</p>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="pricing-section container">
        <Element name="pricing">
          <div className="section-header">
            <h2 className="section-title">Simple, honest pricing</h2>
            <p className="section-subtitle">No hidden fees. Cancel anytime.</p>
          </div>

          {errorMsg && (
            <div className="alert alert--error center" style={{ marginBottom: 16 }}>
              {errorMsg}
            </div>
          )}

          <div className="pricing-grid">
            <div className="pricing-card">
              <div className="pricing-header">
                <h3 className="pricing-name">Basic</h3>
                <div className="pricing-amount">
                  <span className="price">$2</span>
                  <span className="period">/month</span>
                </div>
              </div>
              <ul className="pricing-features">
                <li>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  Daily mindful SMS
                </li>
                <li>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  Universal wisdom themes
                </li>
                <li>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  Custom delivery time
                </li>
                <li>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  Pause anytime
                </li>
              </ul>
              <button className="btn" onClick={() => handleSubscribe('basic')} disabled={loadingPlan === 'basic'}>
                {loadingPlan === 'basic' && <Spinner />}
                {loadingPlan === 'basic' ? 'Starting…' : 'Subscribe Basic'}
              </button>
            </div>

            <div className="pricing-card pricing-featured pricing-disabled">
              <div className="featured-badge coming-soon-badge">Coming Soon</div>
              <div className="pricing-header">
                <h3 className="pricing-name">Premium</h3>
                <div className="pricing-amount">
                  <span className="price">$5</span>
                  <span className="period">/month</span>
                </div>
              </div>
              <ul className="pricing-features">
                <li>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  Everything in Basic
                </li>
                <li>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  Birth chart personalization
                </li>
                <li>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  Transit-based insights
                </li>
                <li>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  Priority feature access
                </li>
              </ul>
              <button className="btn btn-disabled" disabled>
                Coming Soon
              </button>
            </div>
          </div>
        </Element>
      </section>

      {/* About Section */}
      <section id="about" className="about-section container">
        <Element name="about">
          <div className="about-content">
            <div className="about-text">
              <h2 className="section-title">Why Your Highest Self?</h2>
              <p className="about-paragraph">
                In a world of constant noise and distraction, we believe in the power of simplicity. Your Highest Self was born from a simple idea: what if guidance didn't require
                an app, a notification center, or another thing to manage?
              </p>
              <p className="about-paragraph">
                Each message is carefully crafted to offer perspective, encouragement, and a moment of pause. Whether you're seeking daily grounding or exploring your cosmic
                blueprint through astrology, our service meets you where you are—one text at a time.
              </p>
              <p className="about-paragraph">
                We're not here to overwhelm you with content. We're here to offer a single thread of wisdom each day, delivered with intention and respect for your time.
              </p>
            </div>
            <div className="about-values">
              <div className="value-item">
                <div className="value-icon">🌱</div>
                <h3 className="value-title">Intentional</h3>
                <p className="value-desc">Every message serves a purpose</p>
              </div>
              <div className="value-item">
                <div className="value-icon">🔒</div>
                <h3 className="value-title">Private</h3>
                <p className="value-desc">Your data is never shared or sold</p>
              </div>
              <div className="value-item">
                <div className="value-icon">✨</div>
                <h3 className="value-title">Accessible</h3>
                <p className="value-desc">Works on any phone, no app needed</p>
              </div>
            </div>
          </div>
        </Element>
      </section>

      {/* Testimonials */}
      <section className="testimonials-section container">
        <div className="section-header">
          <h2 className="section-title">What early members are saying</h2>
        </div>

        <div className="testimonials-grid">
          <div className="testimonial-card">
            <div className="testimonial-stars">★★★★★</div>
            <p className="testimonial-text">
              "Been using this for about 3 weeks now. I actually look forward to checking my texts in the morning. The messages are thoughtful and it's nice having something positive to start the day with. Way better than doomscrolling first thing."
            </p>
            <div className="testimonial-author">
              <div className="author-avatar">AM</div>
              <div>
                <div className="author-name">Alex M.</div>
                <div className="author-meta">Premium member</div>
              </div>
            </div>
          </div>

          <div className="testimonial-card">
            <div className="testimonial-stars">★★★★★</div>
            <p className="testimonial-text">
              "Honestly wasn't sure about a daily text subscription at first, but after a few weeks I really look forward to it. Some of the messages hit differently depending on what's going on. Simple, thoughtful, and worth the monthly cost."
            </p>
            <div className="testimonial-author">
              <div className="author-avatar">JL</div>
              <div>
                <div className="author-name">Jordan L.</div>
                <div className="author-meta">Basic member</div>
              </div>
            </div>
          </div>

          <div className="testimonial-card">
            <div className="testimonial-stars">★★★★★</div>
            <p className="testimonial-text">"I love that it's just SMS—no app to download, no notifications to manage. The messages arrive at the perfect time each morning and genuinely help me start the day with a better mindset. Been subscribed for 2 months now."</p>
            <div className="testimonial-author">
              <div className="author-avatar">SK</div>
              <div>
                <div className="author-name">Sam K.</div>
                <div className="author-meta">Basic member</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="cta-section container">
        <div className="cta-content">
          <h2 className="cta-title">Ready to elevate your mornings?</h2>
          <p className="cta-subtitle">Start your journey toward daily intention</p>
          <div className="cta-buttons">
            <a
              href="#pricing"
              className="btn btn-primary btn-lg"
              onClick={(e) => {
                e.preventDefault();
                scrollToIdWithOffset('pricing');
              }}
            >
              Get Started Today
              <span className="btn-arrow">→</span>
            </a>
          </div>
          <p className="cta-note">Start for just $2/month · Cancel anytime</p>
        </div>
      </section>

        <Footer />
      </main>
    </>
  );
}
